import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Trash2, RotateCcw, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/axios';

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const diasRestantes = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));

export const PapeleraCasosDialog = ({ open, onOpenChange, onCambio }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [accion, setAccion] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get('/cases/papelera')).data); } catch { setData({ items: [], total: 0 }); }
    setSel([]);
  }, []);

  useEffect(() => { if (open) { setData(null); setConfirmar(null); load(); } }, [open, load]);

  const ejecutar = async (tipo) => {
    setAccion(tipo);
    try {
      if (tipo === 'restaurar') {
        const r = await api.post('/cases/papelera/restaurar', { ids: sel });
        toast.success(`${r.data.restaurados} caso(s) restaurados${r.data.conflictos ? ` · ${r.data.conflictos} ya existían` : ''}`);
      } else if (tipo === 'definitivo') {
        const r = await api.post('/cases/papelera/eliminar-definitivo', { ids: sel });
        toast.success(`${r.data.eliminados} caso(s) eliminados definitivamente`);
      } else {
        const r = await api.post('/cases/papelera/vaciar');
        toast.success(`Papelera vaciada: ${r.data.eliminados} caso(s) eliminados`);
      }
      setConfirmar(null);
      await load();
      onCambio?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar la acción');
    } finally { setAccion(null); }
  };

  const items = data?.items || [];
  const todos = items.length > 0 && sel.length === items.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] flex flex-col" data-testid="papelera-casos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Papelera de casos</DialogTitle>
          <DialogDescription>Los casos eliminados y sus alertas quedan acá {data?.dias || 30} días; después se borran solos.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={!sel.length || !!accion} onClick={() => ejecutar('restaurar')} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="papelera-restaurar-btn">
            {accion === 'restaurar' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RotateCcw size={14} className="mr-1.5" />} Restaurar ({sel.length})
          </Button>
          <Button size="sm" variant="outline" disabled={!sel.length || !!accion || !!confirmar} onClick={() => setConfirmar('definitivo')} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="papelera-eliminar-definitivo-btn">
            <Trash2 size={14} className="mr-1.5" /> Eliminar definitivamente ({sel.length})
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {confirmar ? (
              <>
                <span className="text-sm font-semibold text-red-700">
                  {confirmar === 'vaciar' ? `¿Borrar los ${data?.total} casos para siempre?` : `¿Borrar ${sel.length} caso(s) para siempre?`}
                </span>
                <Button size="sm" variant="outline" onClick={() => setConfirmar(null)} disabled={!!accion} data-testid="papelera-confirmar-no">No</Button>
                <Button size="sm" onClick={() => ejecutar(confirmar)} disabled={!!accion} className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid={confirmar === 'vaciar' ? 'papelera-vaciar-confirmar' : 'papelera-definitivo-confirmar'}>
                  {accion && <Loader2 size={14} className="mr-1.5 animate-spin" />} {confirmar === 'vaciar' ? 'Sí, vaciar' : 'Sí, eliminar'}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => setConfirmar('vaciar')} disabled={!items.length || !!accion} className="bg-red-600 hover:bg-red-700 text-white" data-testid="papelera-vaciar-btn">
                <Trash2 size={14} className="mr-1.5" /> Vaciar papelera
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto rounded-lg border border-slate-200">
          {!data ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : !items.length ? (
            <div className="py-16 flex flex-col items-center gap-2 text-slate-400" data-testid="papelera-vacia"><Inbox size={28} /> La papelera está vacía.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-8"><input type="checkbox" checked={todos} onChange={() => setSel(todos ? [] : items.map((i) => i.id))} data-testid="papelera-sel-todos" /></th>
                  {['Caso', 'Cliente', 'Estado', 'Alertas', 'Eliminado por', 'Se borra en'].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => {
                  const r = it.resumen || {};
                  const marcado = sel.includes(it.id);
                  return (
                    <tr key={it.id} className={marcado ? 'bg-blue-50/60' : 'hover:bg-slate-50'} data-testid={`papelera-fila-${it.id}`}>
                      <td className="px-3 py-2"><input type="checkbox" checked={marcado} onChange={() => setSel(marcado ? sel.filter((x) => x !== it.id) : [...sel, it.id])} data-testid={`papelera-sel-${it.id}`} /></td>
                      <td className="px-3 py-2"><p className="font-semibold text-slate-800">{r.numero_referencia || r.titulo || it.id.slice(0, 8)}</p>{r.numero_referencia && r.titulo && <p className="text-xs text-slate-500 truncate max-w-xs">{r.titulo}</p>}</td>
                      <td className="px-3 py-2"><p className="text-slate-700">{r.cliente_nombre || '—'}</p><p className="font-mono text-xs text-slate-400">{r.cuit || ''}</p></td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.estado || '—'}</td>
                      <td className="px-3 py-2 tabular-nums">{it.alertas_cantidad}</td>
                      <td className="px-3 py-2 text-xs"><p className="text-slate-700">{it.eliminado_por_nombre || it.eliminado_por}</p><p className="text-slate-400">{fecha(it.eliminado_at)}</p></td>
                      <td className="px-3 py-2 text-xs text-slate-600">{diasRestantes(it.expira_at)} días</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PapeleraCasosDialog;
